import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError, track } from "@/lib/api";
import { toast } from "sonner";
import { Check, X } from "lucide-react";
import * as Icons from "lucide-react";

function Icon({ name, className }) { const C = Icons[name] || Icons.Circle; return <C className={className} />; }
const fmt = (n) => (Number.isInteger(Number(n)) ? String(n) : Number(n).toFixed(2).replace(".", ","));

export default function Pricing() {
  const [plans, setPlans] = useState([]);
  const [services, setServices] = useState([]);
  const [yearly, setYearly] = useState(false);
  const [loading, setLoading] = useState("");
  const [wl, setWl] = useState({ email: "", service: "advisory" });
  const [wlSent, setWlSent] = useState(false);

  useEffect(() => {
    api.get("/plans").then(r => setPlans(r.data)).catch(() => {});
    api.get("/services").then(r => setServices(r.data)).catch(() => {});
    track("pricing_viewed");
  }, []);

  const checkout = async (p) => {
    const lookup = yearly ? p.lookup_key_yearly : p.lookup_key_monthly;
    if (!lookup) { window.location.href = "/register"; return; }
    track(`${p.plan_id}_selected`, { billing_period: yearly ? "annual" : "monthly" });
    setLoading(p.plan_id);
    track("checkout_started", { plan: p.plan_id, billing_period: yearly ? "annual" : "monthly" });
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: lookup, quantity: 1, origin_url: window.location.origin,
        billing_period: yearly ? "annual" : "monthly",
      });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiError(e)); setLoading(""); }
  };

  const sendWaitlist = async (e) => {
    e.preventDefault();
    try {
      await api.post("/waitlist", { email: wl.email, service: wl.service });
      track("service_interest_submitted", { service: wl.service });
      setWlSent(true); toast.success("Richiesta registrata!");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const comingSoon = services.filter(s => s.status === "COMING_SOON");

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <div className="text-center">
        <span className="ic-badge status-active mb-4"><span className="w-1.5 h-1.5 rounded-full bg-[#10B981]" />Prezzi trasparenti, sempre + IVA</span>
        <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight">Scegli il piano che fa per la tua impresa</h1>
        <p className="mt-4 text-lg text-[#524F5E] max-w-2xl mx-auto">Inizia gratis, senza carta. Cresci quando serve più controllo.</p>
        <div className="mt-6 inline-flex items-center bg-white rounded-full p-1 border border-[#E5E4EB]">
          <button onClick={() => setYearly(false)} className={`px-5 py-2 rounded-full text-sm font-semibold ${!yearly ? 'bg-[#5424D5] text-white' : 'text-[#524F5E]'}`} data-testid="billing-monthly">Mensile</button>
          <button onClick={() => setYearly(true)} className={`px-5 py-2 rounded-full text-sm font-semibold ${yearly ? 'bg-[#5424D5] text-white' : 'text-[#524F5E]'}`} data-testid="billing-yearly">Annuale</button>
        </div>
        {yearly && <p className="mt-2 text-xs font-semibold text-[#047857]">Risparmia scegliendo il piano annuale</p>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mt-12 items-stretch">
        {plans.map(p => {
          const price = yearly ? (p.price_yearly ?? null) : p.price_monthly;
          return (
            <div key={p.plan_id} className={`ic-card p-6 flex flex-col ${p.popular ? 'border-[#5424D5] ring-2 ring-[#5424D5]/20' : ''}`} data-testid={`plan-${p.plan_id}`}>
              <div className="flex items-center gap-2 mb-3 min-h-[26px]">
                {p.popular && <span className="ic-badge status-coming">{p.badge}</span>}
                {p.label && <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#7A7788]">{p.label}</span>}
              </div>
              <h3 className="text-xl font-bold">{p.name}</h3>
              {p.tagline && <p className="text-xs text-[#524F5E] mt-1 min-h-[2.5rem]">{p.tagline}</p>}
              <div className="mt-4">
                {price === null ? (
                  <span className="text-lg font-bold text-[#7A7788]">In definizione</span>
                ) : (
                  <>
                    <span className="text-4xl font-extrabold">€{fmt(price)}</span>
                    <span className="text-sm text-[#7A7788]">/{yearly ? "anno" : "mese"}</span>
                    {p.price_monthly > 0 && (
                      <>
                        <p className="text-xs text-[#7A7788] font-semibold">+ IVA</p>
                        {yearly && p.yearly_equiv && <p className="text-xs text-[#047857] font-semibold mt-1">equivalente a {p.yearly_equiv}</p>}
                      </>
                    )}
                  </>
                )}
              </div>
              <ul className="mt-4 space-y-1.5 flex-1">
                {p.features?.map((f, i) => (
                  <li key={i} className="flex gap-2 text-xs text-[#524F5E]"><Check className="w-3.5 h-3.5 text-[#5424D5] flex-shrink-0 mt-0.5" />{f}</li>
                ))}
                {p.excluded?.map((f, i) => (
                  <li key={`x${i}`} className="flex gap-2 text-xs text-[#7A7788]"><X className="w-3.5 h-3.5 text-[#D8D6E2] flex-shrink-0 mt-0.5" />{f}</li>
                ))}
              </ul>
              {p.plan_id === "free" ? (
                <Link to="/register" className="ic-btn-ghost mt-5 text-sm text-center" data-testid="plan-cta-free">{p.cta_text}</Link>
              ) : (
                <button onClick={() => checkout(p)} disabled={loading === p.plan_id}
                  className={`mt-5 text-sm ${p.popular ? 'ic-btn-primary' : 'ic-btn-ghost'}`}
                  data-testid={`plan-cta-${p.plan_id}`}>
                  {loading === p.plan_id ? "Reindirizzamento…" : p.cta_text}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* SERVIZI AGGIUNTIVI — senza prezzi */}
      <div className="mt-20">
        <span className="ic-badge status-coming mb-3">Disponibili prossimamente</span>
        <h2 className="text-3xl font-bold tracking-tight">Servizi aggiuntivi</h2>
        <p className="mt-2 text-[#524F5E]">Report e analisi professionali una tantum. Prezzi in definizione — lascia la tua email per essere avvisato.</p>
        <div className="grid md:grid-cols-3 gap-5 mt-8">
          {comingSoon.slice(0, 9).map(s => (
            <div key={s.service_id} className="ic-card p-6" data-testid={`addon-${s.service_id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="w-10 h-10 rounded-xl bg-[#EDE8FC] text-[#5424D5] flex items-center justify-center"><Icon name={s.icon} className="w-5 h-5" /></div>
                <span className="ic-badge status-coming">Coming Soon</span>
              </div>
              <h4 className="font-semibold">{s.service_name}</h4>
              <p className="text-sm text-[#524F5E] mt-1">{s.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ADVISORY UMANO */}
      <div className="mt-20 ic-card p-10 bg-[#171522] text-white border-none">
        <p className="text-xs uppercase tracking-widest text-[#FF6A24] font-bold mb-3">ImpresaChiara Advisory</p>
        <h2 className="text-2xl md:text-3xl font-bold">Hai bisogno di supporto professionale dedicato?</h2>
        <p className="mt-3 text-white/70 max-w-2xl">Call con controller, revisione economico-finanziaria, business plan verificato, dossier bancario, analisi investimento, supporto finanziamenti. La consulenza umana è separata dai piani SaaS.</p>
        {wlSent ? (
          <p className="mt-6 font-semibold text-[#10B981]" data-testid="advisory-sent">Richiesta registrata. Ti contatteremo a breve.</p>
        ) : (
          <form onSubmit={sendWaitlist} className="mt-6 flex flex-wrap gap-3">
            <input required type="email" placeholder="La tua email" value={wl.email} onChange={e => setWl({ ...wl, email: e.target.value })}
              className="px-4 py-3 rounded-full bg-white/10 border border-white/20 text-white placeholder-white/50 min-w-[260px] focus:outline-none focus:border-[#FF6A24]"
              data-testid="advisory-email" />
            <select value={wl.service} onChange={e => setWl({ ...wl, service: e.target.value })}
              className="px-4 py-3 rounded-full bg-white/10 border border-white/20 text-white" data-testid="advisory-service">
              <option value="advisory">Parla con un esperto</option>
              {comingSoon.map(s => <option key={s.service_id} value={s.slug}>{s.service_name}</option>)}
            </select>
            <button type="submit" className="ic-btn-accent" data-testid="advisory-submit">Richiedi informazioni</button>
          </form>
        )}
      </div>
    </div>
  );
}
